import React, { ComponentProps, useCallback, useState } from 'react';

import requestPutHeroPlannerSchedulePushStatus from '~/queries/customerCommunication/requestPutHeroPlannerSchedulePushStatus';

import { Alert, AlertTitle, Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';

import { RequestStatus } from '~/consts/requestConstants';

import { isRequestPending, isRequestRejected } from '~/utils/requestUtils';

interface Props {
  pushStatus: CustomerCommunication.HeroPlannerSchedule['pushStatus'];
  scheduleId: string;
  segment?: string;
  onStatusUpdate: () => void;
}

function SchedulePushStatusEditor({ pushStatus, scheduleId, segment, onStatusUpdate }: Props) {
  const [updateReq, setUpdateReq] = useState<
    Utils.RequestState<CustomerCommunication.HeroPlannerSchedule['pushStatus'], string>
  >({
    status: RequestStatus.INITIAL,
  });
  const updateStatus = useCallback(
    async (newStatus: CustomerCommunication.HeroPlannerSchedule['pushStatus']) => {
      setUpdateReq({
        status: RequestStatus.PENDING,
        params: undefined,
      });

      try {
        const result = await requestPutHeroPlannerSchedulePushStatus(scheduleId, newStatus, segment);
        setUpdateReq({
          status: RequestStatus.FULFILLED,
          params: undefined,
          result,
        });
        onStatusUpdate();
      } catch (error) {
        setUpdateReq({
          status: RequestStatus.REJECTED,
          params: undefined,
          error,
        });
      }
    },
    [scheduleId, onStatusUpdate],
  );

  const handleChange = useCallback<ComponentProps<typeof ToggleButtonGroup>['onChange']>(
    async (event, value) => {
      if (value) updateStatus(value);
    },
    [updateStatus],
  );

  return (
    <Stack direction="column" gap={1} position="relative" flexGrow="1">
      <ToggleButtonGroup
        value={pushStatus}
        exclusive
        disabled={isRequestPending(updateReq)}
        fullWidth
        onChange={handleChange}
      >
        <ToggleButton value="DRAFT" color="info">
          DRAFT
        </ToggleButton>
        <ToggleButton value="PENDING" color="warning">
          PENDING
        </ToggleButton>
        <ToggleButton value="APPROVED" color="success">
          APPROVED
        </ToggleButton>
      </ToggleButtonGroup>
      {isRequestRejected(updateReq) && (
        <Alert severity="error">
          <AlertTitle>Could not update status</AlertTitle>
          {updateReq.error}
        </Alert>
      )}
    </Stack>
  );
}

export default SchedulePushStatusEditor;
